/**
 * AdminDatenbankSection — Datenbank-Dokumentation (nur Admin)
 * 
 * Zeigt Schema-Übersicht, Tabellen-Beschreibungen und KI-Anweisungen
 * für die Datenbank-Architektur.
 */

import { useState } from 'react';
import {
  Database, Table2, ChevronDown, ChevronRight, Shield, Key,
  FileText, Cpu, Server, HardDrive, Copy, Check, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

interface TableDoc {
  name: string;
  beschreibung: string;
  kategorie: string;
  spalten: string[];
  hinweise?: string;
}

const DB_TABLES: TableDoc[] = [
  // Kern
  { name: 'mitarbeiter', beschreibung: 'Alle Mitarbeiter mit Rollen, Kontaktdaten und Auth-Verknüpfung', kategorie: 'Kern', spalten: ['id (UUID)', 'vorname', 'nachname', 'email', 'rolle', 'telefon', 'position', 'avatar_url', 'aktiv', 'auth_user_id'] },
  { name: 'betriebe', beschreibung: 'Geschäftsbereiche (Gutachten, Autoglas, Mietwagen, etc.) mit Firmendaten', kategorie: 'Kern', spalten: ['id (UUID)', 'typ', 'name', 'firma', 'strasse', 'plz', 'ort', 'telefon', 'email', 'website', 'iban', 'briefkopf_html', 'fusszeile_html'] },
  { name: 'akten', beschreibung: 'Haupttabelle für alle Schadensfälle/Akten mit Kundendaten', kategorie: 'Kern', spalten: ['id (UUID)', 'aktenzeichen', 'business_unit', 'status', 'kunde_name', 'kennzeichen', 'sachbearbeiter_id', 'erstellt_am'] },
  { name: 'aufgaben', beschreibung: 'Aufgaben und Wiedervorlagen, zuweisbar an Mitarbeiter', kategorie: 'Kern', spalten: ['id (UUID)', 'titel', 'beschreibung', 'status', 'prioritaet', 'business_unit', 'zugewiesen_an', 'erstellt_von', 'faellig_am'], hinweise: 'FK: zugewiesen_an → mitarbeiter.id, erstellt_von → mitarbeiter.id' },
  { name: 'termine', beschreibung: 'Kalendertermine (lokal + Google Calendar Sync)', kategorie: 'Kern', spalten: ['id (UUID)', 'titel', 'beschreibung', 'start_zeit', 'end_zeit', 'ort', 'typ', 'mitarbeiter_id', 'google_event_id'] },
  // Kommunikation
  { name: 'chat_rooms', beschreibung: 'Chat-Räume für interne Kommunikation', kategorie: 'Kommunikation', spalten: ['id (UUID)', 'name', 'typ', 'erstellt_von', 'erstellt_am'] },
  { name: 'chat_messages', beschreibung: 'Chat-Nachrichten mit Dateianhängen', kategorie: 'Kommunikation', spalten: ['id (UUID)', 'room_id', 'sender_id', 'content', 'type', 'file_url', 'erstellt_am'] },
  { name: 'chat_participants', beschreibung: 'Teilnehmer pro Chat-Raum', kategorie: 'Kommunikation', spalten: ['id', 'room_id', 'user_id', 'joined_at'] },
  { name: 'notifications', beschreibung: 'Push-Benachrichtigungen für Mitarbeiter', kategorie: 'Kommunikation', spalten: ['id (UUID)', 'user_id', 'titel', 'nachricht', 'typ', 'gelesen', 'erstellt_am'] },
  // Gutachten
  { name: 'begutachtungen', beschreibung: 'Gutachten-Details: Fahrzeugdaten, Schadensbeschreibung', kategorie: 'Gutachten', spalten: ['id', 'akte_id', 'fahrzeug_typ', 'hersteller', 'modell', 'erstzulassung', 'km_stand'] },
  { name: 'kalkulationen', beschreibung: 'Schadenskalkulationen mit Positionen', kategorie: 'Gutachten', spalten: ['id', 'akte_id', 'reparaturkosten_netto', 'reparaturkosten_brutto', 'wiederbeschaffungswert'] },
  { name: 'lichtbilder', beschreibung: 'Fotos zu Gutachten (Schadensdokumentation)', kategorie: 'Gutachten', spalten: ['id', 'akte_id', 'url', 'beschreibung', 'reihenfolge'] },
  // CRM
  { name: 'crm_kontakte', beschreibung: 'CRM-Kontakte (Kunden, Partner, Werkstätten)', kategorie: 'CRM', spalten: ['id', 'name', 'firma', 'email', 'telefon', 'typ', 'quelle'] },
  { name: 'crm_vorgaenge', beschreibung: 'CRM-Vorgänge/Deals in der Pipeline', kategorie: 'CRM', spalten: ['id', 'kontakt_id', 'titel', 'wert', 'stage', 'status'] },
  // Finanzen
  { name: 'rechnungen', beschreibung: 'Rechnungen mit Positionen und DATEV-Export', kategorie: 'Finanzen', spalten: ['id', 'akte_id', 'rechnungsnummer', 'betrag_netto', 'betrag_brutto', 'status', 'bezahlt_am'] },
  { name: 'zahlungseingaenge', beschreibung: 'Zahlungseingänge und Zuordnung zu Rechnungen', kategorie: 'Finanzen', spalten: ['id', 'rechnung_id', 'betrag', 'datum', 'zahlungsart'] },
  // Admin
  { name: 'admin_zugangsdaten', beschreibung: 'API-Keys und Zugangsdaten (RLS: nur Admin)', kategorie: 'Admin', spalten: ['id', 'kategorie', 'dienst', 'schluessel', 'wert', 'beschreibung', 'ist_geheim'], hinweise: 'RLS-Policy: Nur Admin-Rolle kann lesen/schreiben' },
  { name: 'marken_config', beschreibung: 'Marken-Konfiguration mit Design und KI-Anweisungen (RLS: nur Admin)', kategorie: 'Admin', spalten: ['id', 'marke', 'anzeigename', 'beschreibung', 'farbe_primaer', 'farbe_sekundaer', 'domain', 'ki_anweisungen', 'design_richtlinien'], hinweise: 'RLS-Policy: Nur Admin-Rolle kann lesen/schreiben' },
];

const KATEGORIE_COLORS: Record<string, { color: string; bg: string }> = {
  'Kern': { color: '#2563EB', bg: 'rgba(37,99,235,0.08)' },
  'Kommunikation': { color: '#7C3AED', bg: 'rgba(124,58,237,0.08)' },
  'Gutachten': { color: '#059669', bg: 'rgba(5,150,105,0.08)' },
  'CRM': { color: '#D97706', bg: 'rgba(217,119,6,0.08)' },
  'Finanzen': { color: '#DC2626', bg: 'rgba(220,38,38,0.08)' },
  'Admin': { color: '#9B1C1C', bg: 'rgba(155,28,28,0.08)' },
};

export default function AdminDatenbankSection() {
  const [expandedTable, setExpandedTable] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const categories = Array.from(new Set(DB_TABLES.map(t => t.kategorie)));

  const handleCopySchema = () => {
    const schema = DB_TABLES.map(t =>
      `-- ${t.name}: ${t.beschreibung}\n-- Spalten: ${t.spalten.join(', ')}${t.hinweise ? `\n-- Hinweis: ${t.hinweise}` : ''}`
    ).join('\n\n');
    navigator.clipboard.writeText(schema);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Schema in Zwischenablage kopiert');
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Database className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900">Datenbank-Dokumentation</h2>
              <p className="text-xs text-gray-400">Schema-Übersicht, Tabellen und Anweisungen für die KI</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopySchema}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
              Schema kopieren
            </button>
            <a
              href="https://supabase.com/dashboard/project/ytwxlbpjsjseaqhvyvlh/editor"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-white bg-[#9B1C1C] hover:bg-[#7F1D1D] transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Supabase öffnen
            </a>
          </div>
        </div>

        {/* Statistik */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-gray-800">{DB_TABLES.length}</p>
            <p className="text-[10px] text-gray-400">Tabellen</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-gray-800">{categories.length}</p>
            <p className="text-[10px] text-gray-400">Kategorien</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-gray-800">{DB_TABLES.filter(t => t.hinweise?.includes('RLS')).length}</p>
            <p className="text-[10px] text-gray-400">RLS-geschützt</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-emerald-600 text-sm font-medium">Supabase</p>
            <p className="text-[10px] text-gray-400">PostgreSQL</p>
          </div>
        </div>
      </div>

      {/* KI-Anweisungen für Datenbank */}
      <div className="bg-white rounded-xl border border-amber-200 p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Cpu className="w-4 h-4 text-amber-600" />
          <h3 className="text-xs font-bold text-gray-800">KI-Anweisungen für Datenbank-Operationen</h3>
        </div>
        <div className="bg-amber-50 rounded-lg p-4 text-xs text-gray-700 space-y-2 font-mono">
          <p>1. Alle Tabellen befinden sich im Schema "public" auf Supabase (PostgreSQL).</p>
          <p>2. Zugriff über Supabase JS Client: <code className="bg-white px-1 rounded">supabase.from('tabelle').select('*')</code></p>
          <p>3. UUIDs werden automatisch generiert (<code className="bg-white px-1 rounded">gen_random_uuid()</code>).</p>
          <p>4. Timestamps verwenden <code className="bg-white px-1 rounded">TIMESTAMPTZ</code> mit Default <code className="bg-white px-1 rounded">now()</code>.</p>
          <p>5. Row Level Security (RLS) ist auf admin_zugangsdaten und marken_config aktiv.</p>
          <p>6. Foreign Keys: aufgaben.zugewiesen_an → mitarbeiter.id</p>
          <p>7. Business Units: gutachten, autoglas, mietwagen, werkstatt, protex, unfallgiganten, unfallwiki, airbnb, recht, app_programmierung</p>
          <p>8. Supabase URL: https://ytwxlbpjsjseaqhvyvlh.supabase.co</p>
        </div>
      </div>

      {/* Tabellen nach Kategorie */}
      {categories.map(kategorie => {
        const tables = DB_TABLES.filter(t => t.kategorie === kategorie);
        const catColor = KATEGORIE_COLORS[kategorie] || { color: '#6B7280', bg: 'rgba(107,114,128,0.08)' };

        return (
          <div key={kategorie}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: catColor.color }} />
              <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{kategorie}</h3>
              <span className="text-[10px] text-gray-300">{tables.length} Tabellen</span>
            </div>

            <div className="space-y-1.5">
              {tables.map(table => {
                const isExpanded = expandedTable === table.name;
                return (
                  <div key={table.name} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => setExpandedTable(isExpanded ? null : table.name)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors"
                    >
                      <Table2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: catColor.color }} />
                      <code className="text-xs font-mono font-bold text-gray-800">{table.name}</code>
                      <span className="text-[11px] text-gray-400 flex-1 text-left truncate">{table.beschreibung}</span>
                      {table.hinweise?.includes('RLS') && (
                        <Shield className="w-3 h-3 text-amber-500 flex-shrink-0" />
                      )}
                      {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
                    </button>

                    {isExpanded && (
                      <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/50">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Spalten</p>
                        <div className="flex flex-wrap gap-1.5">
                          {table.spalten.map(col => (
                            <span key={col} className="text-[10px] font-mono bg-white px-2 py-1 rounded border border-gray-200 text-gray-600">
                              {col}
                            </span>
                          ))}
                        </div>
                        {table.hinweise && (
                          <div className="mt-2 flex items-start gap-1.5">
                            <FileText className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                            <p className="text-[10px] text-gray-500">{table.hinweise}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
